import { getPlatform } from 'src/utils/platform';
import * as Sentry from '@sentry/capacitor';
import { reportError } from 'src/modules/logs/Sentry';
import { getAppVersion } from 'src/modules/appVersion';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';
import type { CapturePhase } from 'src/modules/camera/webCameraTypes';
import { LocalStorageKey } from 'src/config/constants';
import { generatePath, ROUTES } from 'src/config/routes';

const SESSION_MAX_AGE_MS = 5 * 60 * 1000;

export interface CameraSession {
  route: string;
  lessonId?: number;
  examId?: number;
  subject?: string;
  childId?: number;
  source: 'camera' | 'gallery';
  timestamp: number;
  platform: string;
  heapUsedMB?: number;
  photosCount?: number;
  // Saved at the tap so a kill, which leaves no JS to report it, still knows the arm.
  controlFeatVariant?: string;
  captureMode?: 'web' | 'native';
  phase?: CapturePhase;
  phaseAt?: number;
  lastVisibility?: 'visible' | 'hidden';
  hiddenAt?: number;
}

export const getHeapUsedMB = (): number | undefined => {
  try {
    const memory = Reflect.get(performance, 'memory') as { usedJSHeapSize: number } | undefined;
    if (!memory) return undefined;
    return Math.round(memory.usedJSHeapSize / 1024 / 1024);
  } catch {
    return undefined;
  }
};

export const saveCameraSession = (
  session: Omit<CameraSession, 'timestamp' | 'platform' | 'heapUsedMB'>,
) => {
  try {
    const data: CameraSession = {
      ...session,
      timestamp: Date.now(),
      platform: getPlatform(),
      heapUsedMB: getHeapUsedMB(),
    };
    localStorage.setItem(LocalStorageKey.CAMERA_SESSION, JSON.stringify(data));
    Sentry.addBreadcrumb({
      category: 'camera',
      message: 'Camera session saved before camera capture',
      level: 'info',
      data,
    });
  } catch { /* */ }
};

// Patches the session saved at the tap; a no-op when none was saved.
export type CameraSessionPatch = Partial<
  Pick<CameraSession, 'controlFeatVariant' | 'captureMode' | 'phase' | 'phaseAt' | 'lastVisibility' | 'hiddenAt'>
>;

export const updateCameraSession = (patch: CameraSessionPatch) => {
  try {
    const raw = localStorage.getItem(LocalStorageKey.CAMERA_SESSION);
    if (!raw) return;
    const data: CameraSession = { ...JSON.parse(raw), ...patch };
    localStorage.setItem(LocalStorageKey.CAMERA_SESSION, JSON.stringify(data));
  } catch { /* */ }
};

export const clearCameraSession = () => {
  try {
    localStorage.removeItem(LocalStorageKey.CAMERA_SESSION);
  } catch { /* */ }
};

export const getOrphanedCameraSession = (): CameraSession | null => {
  try {
    const raw = localStorage.getItem(LocalStorageKey.CAMERA_SESSION);
    if (!raw) return null;
    const session: CameraSession = JSON.parse(raw);
    const age = Date.now() - session.timestamp;
    if (age > SESSION_MAX_AGE_MS) {
      clearCameraSession();
      return null;
    }
    return session;
  } catch {
    clearCameraSession();
    return null;
  }
};

// Written by initAnalytics after its async Device.getInfo(), so this reads the previous launch's
// values; absent on the very first launch.
const readStoredDeviceProfile = (): { deviceModel?: string; deviceOsVersion?: string } => {
  try {
    const raw = localStorage.getItem(LocalStorageKey.DEVICE_PROFILE);
    if (!raw) return {};
    const { model, osVersion } = JSON.parse(raw);
    return {
      ...(typeof model === 'string' && { deviceModel: model }),
      ...(typeof osVersion === 'string' && { deviceOsVersion: osVersion }),
    };
  } catch {
    return {};
  }
};

export const detectOrphanedCameraSession = (): CameraSession | null => {
  const session = getOrphanedCameraSession();
  if (!session) return null;

  const ageMs = Date.now() - session.timestamp;
  reportError('Camera session lost: app was killed during camera activity', {
    ...session,
    ageMs,
    ageSeconds: Math.round(ageMs / 1000),
  });
  logAnalyticsEvent('camera_session_killed', {
    route: session.route,
    lessonId: session.lessonId,
    examId: session.examId,
    subject: session.subject,
    source: session.source,
    platform: session.platform,
    heapUsedMB: session.heapUsedMB,
    photosCount: session.photosCount,
    ageSeconds: Math.round(ageMs / 1000),
    jsBootMs: Math.round(performance.now()),
    appVersion: getAppVersion(),
    ...(session.controlFeatVariant !== undefined && {
      control_feat_variant: session.controlFeatVariant,
    }),
    ...(session.captureMode && { captureMode: session.captureMode }),
    ...(session.phase && { phase: session.phase }),
    ...(typeof session.phaseAt === 'number' && { sessionAgeMs: Math.max(0, session.phaseAt - session.timestamp) }),
    ...(session.lastVisibility && { lastVisibility: session.lastVisibility }),
    ...(session.lastVisibility === 'hidden' &&
      typeof session.hiddenAt === 'number' && { hiddenForMs: Math.max(0, Date.now() - session.hiddenAt) }),
    ...readStoredDeviceProfile(),
  });
  clearCameraSession();
  return session;
};

const NEW_EXAM_PHOTO_ROUTE = generatePath('EXAM_PHOTO', { examId: 'new' });

export const SUBJECTS_ROUTE_PREFIX = ROUTES.SUBJECT_SELECT_MEDIA.path.slice(
  0,
  ROUTES.SUBJECT_SELECT_MEDIA.path.indexOf(':'),
);

const CREATION_FLOW_PHOTO_ROUTE = new RegExp(
  `^${ROUTES.SUBJECT_SELECT_MEDIA.path.replace(/:\w+/g, '[^/]+')}(\\?|$)`,
);

const isCreationFlowPhotoRoute = (route: string) =>
  CREATION_FLOW_PHOTO_ROUTE.test(route);

export const buildCameraRedirectPath = (session: CameraSession): string | null => {
  // Before the lesson id: the flow's photo screen creates a draft lesson while the photos upload,
  // so a capture from inside the flow carries one too.
  if (session.route && isCreationFlowPhotoRoute(session.route)) {
    const [pathname, ...searchParts] = session.route.split('?');
    const params = new URLSearchParams(searchParts.join('?'));
    // The draft lesson only ever lives in the session: the flow's photo screen keeps it in state,
    // never in its URL.
    if (session.lessonId && !params.has('lessonId')) {
      params.set('lessonId', String(session.lessonId));
    }
    if (session.childId && !params.has('childId')) {
      params.set('childId', String(session.childId));
    }
    const query = params.toString();
    return query ? `${pathname}?${query}` : pathname;
  }
  if (session.lessonId) {
    return generatePath('LESSON_PHOTOS_EDIT', { lessonId: session.lessonId });
  }
  if (session.examId) {
    return generatePath('EXAM_PHOTO', { examId: session.examId });
  }
  if (session.route) {
    const [pathname, ...searchParts] = session.route.split('?');
    const params = new URLSearchParams(searchParts.join('?'));
    if (session.childId && !params.has('childId')) {
      params.set('childId', String(session.childId));
    }
    // The new-exam page reads subject and child from navigation state, which a post-kill redirect
    // lacks; without them it sends the user home.
    if (pathname === NEW_EXAM_PHOTO_ROUTE) {
      if (session.subject && !params.has('subject')) params.set('subject', session.subject);
      if (session.childId && !params.has('targetChildUserId')) {
        params.set('targetChildUserId', String(session.childId));
      }
    }
    const query = params.toString();
    return query ? `${pathname}?${query}` : pathname;
  }
  return null;
};

// Rebuilds the navigation state from the redirect path; only the new-exam page takes state.
export const cameraRedirectNavigationState = (
  path: string,
): { subject: string; targetChildUserId?: number } | undefined => {
  const [pathname, ...searchParts] = path.split('?');
  if (pathname !== NEW_EXAM_PHOTO_ROUTE) return undefined;
  const params = new URLSearchParams(searchParts.join('?'));
  const subject = params.get('subject');
  if (!subject) return undefined;
  const child = Number(params.get('targetChildUserId'));
  return {
    subject,
    ...(Number.isInteger(child) && child > 0 && { targetChildUserId: child }),
  };
};

export interface CameraRedirectIntent {
  path: string;
  lessonId?: number;
  examId?: number;
  timestamp: number;
}

export const setPendingCameraRedirect = (intent: Omit<CameraRedirectIntent, 'timestamp'>) => {
  try {
    localStorage.setItem(
      LocalStorageKey.CAMERA_REDIRECT,
      JSON.stringify({ ...intent, timestamp: Date.now() }),
    );
  } catch { /* */ }
};

export const consumePendingCameraRedirect = (): CameraRedirectIntent | null => {
  try {
    const raw = localStorage.getItem(LocalStorageKey.CAMERA_REDIRECT);
    if (!raw) return null;
    localStorage.removeItem(LocalStorageKey.CAMERA_REDIRECT);
    const intent: CameraRedirectIntent = JSON.parse(raw);
    if (Date.now() - intent.timestamp > SESSION_MAX_AGE_MS) return null;
    return intent;
  } catch {
    try { localStorage.removeItem(LocalStorageKey.CAMERA_REDIRECT); } catch { /* */ }
    return null;
  }
};

type RestoredPickedPhoto = { webPath?: string; path?: string };

type RestoredPhotoData = RestoredPickedPhoto & { photos?: RestoredPickedPhoto[] };

export const extractPhotosFromRestoredResult = (
  rawData: unknown,
  methodName: string,
): { webPath: string; path?: string }[] => {
  if (!rawData || typeof rawData !== 'object') return [];
  const data: RestoredPhotoData = rawData;
  if (methodName === 'getPhoto' && data.webPath) {
    return [{ webPath: data.webPath, path: data.path }];
  }
  if (methodName === 'pickImages' && Array.isArray(data.photos)) {
    return data.photos
      .filter(p => !!p.webPath)
      .map(p => ({ webPath: p.webPath as string, path: p.path }));
  }
  return [];
};

const CANCEL_MESSAGE_RE = /cancel|denied access/i;

export const classifyRestoredResult = (
  result: { success: boolean; data?: unknown; error?: { message?: string } } | null,
  methodName: string,
): 'photo' | 'cancelled' | 'error' | 'none' => {
  if (!result) return 'none';
  if (result.success) {
    return extractPhotosFromRestoredResult(result.data, methodName).length ? 'photo' : 'cancelled';
  }
  return CANCEL_MESSAGE_RE.test(result.error?.message ?? '') ? 'cancelled' : 'error';
};

export interface RestoredPhoto {
  webPath: string;
  path?: string;
}

export interface RestoredPhotoScope {
  lessonId?: number;
  examId?: number;
  route?: string;
}

let restoredCameraData: ({ photos: RestoredPhoto[] } & RestoredPhotoScope) | null = null;
const restoredListeners = new Set<() => void>();

// Pull-on-mount is the common case; this covers a result arriving after the grid already rendered.
export const subscribeRestoredCameraPhotos = (listener: () => void): (() => void) => {
  restoredListeners.add(listener);
  return () => {
    restoredListeners.delete(listener);
  };
};

export const setRestoredCameraPhotos = (
  photos: RestoredPhoto[],
  scope: RestoredPhotoScope,
) => {
  restoredCameraData = { photos, ...scope };
  restoredListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A listener must never break the boot path that set the photos.
    }
  });
};

const takeRestored = ({ photos }: { photos: RestoredPhoto[] }): RestoredPhoto[] => {
  restoredCameraData = null;
  return photos;
};

export const consumeRestoredLessonPhotos = (
  lessonId: number | undefined,
): RestoredPhoto[] | null => {
  if (!restoredCameraData || lessonId === undefined) return null;
  if (restoredCameraData.lessonId !== lessonId) return null;
  return takeRestored(restoredCameraData);
};

export const consumeRestoredExamPhotos = (
  examId: number | undefined,
): RestoredPhoto[] | null => {
  if (!restoredCameraData) return null;
  if (examId === undefined) {
    // The new-exam screen has no exam yet; match on the saved route.
    const isNewExamCapture =
      restoredCameraData.examId === undefined &&
      !!restoredCameraData.route?.startsWith(NEW_EXAM_PHOTO_ROUTE);
    return isNewExamCapture ? takeRestored(restoredCameraData) : null;
  }
  if (restoredCameraData.examId !== examId) return null;
  return takeRestored(restoredCameraData);
};

// For grids without a lesson or exam id (timetable): matched by saved route.
export const consumeRestoredRoutePhotos = (routePrefix: string): RestoredPhoto[] | null => {
  if (!restoredCameraData) return null;
  if (restoredCameraData.lessonId !== undefined || restoredCameraData.examId !== undefined) return null;
  if (!restoredCameraData.route?.startsWith(routePrefix)) return null;
  return takeRestored(restoredCameraData);
};
